import moment from 'moment';
import { useRef, useMemo } from 'react';

import i18n from 'src/locales/i18n';
import { useGetJobInvitesByJobQuery } from 'src/services/jobs/jobs.service';
import { JobInvite, JobVerificationStatus } from 'src/services/jobs/jobs.types';
import { useGetJobCandidatesQuery } from 'src/services/applications/applications.service';
import { JobApplication, ApplicationStage } from 'src/services/applications/applications.types';

import { TenantType } from 'src/types/enums';
import { CardType, IKanbanColumn, IKanbanApplicationCard } from 'src/types/kanban';

// ----------------------------------------------------------------------

export const RecruiterApplicationStages = [
  ApplicationStage.INVITED,
  ApplicationStage.SCREENING,
  ApplicationStage.INTERNAL_INTERVIEW,
  ApplicationStage.READY_TO_SHORTLIST,
  ApplicationStage.SHORTLISTED,
  ApplicationStage.READY_TO_INTERVIEW,
  ApplicationStage.INTERVIEW,
  ApplicationStage.REFERENCE_CHECK,
  ApplicationStage.READY_TO_OFFER, // No candidate notification
  ApplicationStage.OFFER,
  ApplicationStage.OFFER_ACCEPTED,
  ApplicationStage.TO_BE_REJECTED,
  ApplicationStage.REJECTED,
  ApplicationStage.WITHDRAWN,
];

export const ClientApplicationStages = [
  ApplicationStage.INVITED,
  ApplicationStage.SHORTLISTED,
  ApplicationStage.INTERVIEW,
  ApplicationStage.REFERENCE_CHECK,
  ApplicationStage.READY_TO_OFFER,
  ApplicationStage.OFFER,
  ApplicationStage.OFFER_ACCEPTED,
  ApplicationStage.TO_BE_REJECTED,
  ApplicationStage.REJECTED,
  ApplicationStage.WITHDRAWN,
];

export const ApplicationStageDivergence = RecruiterApplicationStages.filter(
  (stage) => !ClientApplicationStages.includes(stage)
);

export type BoardConfig = {
  board: { [key: string]: IKanbanColumn<IKanbanApplicationCard> } | null | undefined;
  /** First load only — no board to show yet. Drives the column skeletons. */
  boardLoading: boolean;
  /** Any request in flight, including a reload after a search changes. */
  boardFetching: boolean;
  boardError: boolean;
  boardEmpty: boolean;
};

export const TenantToColumnMap = {
  [TenantType.Recruiter]: RecruiterApplicationStages,
  [TenantType.Client]: ClientApplicationStages,
};

const generateBoard = (
  applications: JobApplication[],
  invites: JobInvite[],
  tenant: TenantType
): { [key: string]: IKanbanColumn<IKanbanApplicationCard> } => {

  const applicationsMap: Record<
    string,
    IKanbanColumn<IKanbanApplicationCard>
  > = RecruiterApplicationStages.reduce(
    (acc, stage: ApplicationStage, index: number) => {
      acc[stage] = {
        id: `${stage}`,
        name: i18n.t(`enums.application_stage.${stage}`),
        tooltip: i18n.t(`applications.detail.kanban.stage_tooltips.${stage}`),
        tasks: [],
        order: index + 1,
        stage: stage as string,
      };

      return acc;
    },
    {} as { [key: string]: IKanbanColumn<IKanbanApplicationCard> }
  );

  // eslint-disable-next-line no-restricted-syntax
  for (let i = 0; i < applications.length; i++) {
    const { id, attached_profile, stage, requested_stage, manager_type, recruiter, client, rating, representation_status, job } =
      applications[i];

    const task: IKanbanApplicationCard = {
      id,
      index: i,
      candidateName: `${attached_profile.first_name} ${attached_profile.last_name}`,
      recruiterName: recruiter?.company_profile?.name,
      type: CardType.Application,
      positionTitle: attached_profile.position_title,
      rating,
      avatarUrl: attached_profile.profile_image?.public_path,
      stage,
      isShortlisted: !!applications[i].sent_to_client_at,
      interviewPhase: applications[i].interview_phase,
      requestedStage: requested_stage,
      isExternallyManaged: tenant === TenantType.Client && manager_type === TenantType.Recruiter,
      hasVerifiedClient: !!client && job?.verification_status === JobVerificationStatus.VERIFIED,
      representation_status,
    };

    if (tenant !== TenantType.Candidate) {
      if (requested_stage) {
        applicationsMap[requested_stage]?.tasks.push(task);
      } else {
        applicationsMap[stage]?.tasks.push(task);
      }
    } else {
      applicationsMap[stage]?.tasks.push(task);

    }
  }

  // invites
  for (let i = 0; i < invites.length; i++) {
    const { id, first_name, last_name, job, created_at } = invites[i];

    const task: IKanbanApplicationCard = {
      id,
      index: i,
      candidateName: `${first_name} ${last_name}`,
      recruiterName: job.recruiter_company_name,
      type: CardType.Job_Invite,
      positionTitle: moment(created_at).format('DD/MM/YYYY HH:mm'),
      avatarUrl: undefined,
      stage: ApplicationStage.INVITED,
      requestedStage: undefined,
      isExternallyManaged: false,
      hasVerifiedClient: false,
    };

    applicationsMap[ApplicationStage.INVITED].tasks.push(task);
  }

  return applicationsMap;
};

export function useGetJobApplicationsBoard(jobId: string, currentTenant: TenantType, filters: { search_query?: string } = {}): BoardConfig {
  const {
    currentData: jobApplications,
    isLoading,
    isFetching,
    isError,
  } = useGetJobCandidatesQuery(
    {
      jobId,
      params: {
        page: 1,
        per_page: 999,
        search_query: filters.search_query || undefined
      },
    },
    {
      refetchOnFocus: true,
      refetchOnReconnect: true,
      skip: !jobId,
    }
  );

  const { currentData: jobInvites } = useGetJobInvitesByJobQuery(
    {
      jobId,
      page: 1,
      perPage: 999,
    },
    {
      refetchOnFocus: true,
      refetchOnReconnect: true,
      skip: !jobId,
    }
  );

  const boardRef = useRef<BoardConfig | null>(null);

  const memoizedValue = useMemo(
    () => {
      const returnValue: BoardConfig = {
        board: jobApplications?.results
          ? generateBoard(jobApplications?.results ?? [], jobInvites?.results ?? [], currentTenant)
          : boardRef.current?.board,
        boardLoading: isLoading,
        boardFetching: isFetching,
        boardError: isError,
        boardEmpty: !isLoading && !jobApplications?.count,
      };

      boardRef.current = returnValue;

      return returnValue;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [jobApplications?.results, jobInvites?.results, isError, isLoading, isFetching, currentTenant]
  );

  return memoizedValue;
}
